import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Send, Bot, User as UserIcon, MessageCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const WHATSAPP_NUMBER = "14247281791"; // Support number (Twilio)
const WELCOME = {
  role: "assistant",
  text:
    "Oi! Sou a assistente de suporte da Ultra. Pergunte sobre checklists, estoque, auditorias, relatórios, integrações — o que precisar. Se não conseguir resolver por aqui, encaminho para o time humano no WhatsApp.",
  need_human: false,
};

export default function AISupportChat() {
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    const nextHistory = [...messages, { role: "user", text }];
    setMessages(nextHistory);
    setSending(true);
    try {
      const history = nextHistory
        .filter((m) => m.role !== "welcome")
        .map((m) => ({ role: m.role, text: m.text }));
      const { data } = await api.post("/ai/support-chat", {
        text,
        history,
      });
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: data.reply,
          need_human: !!data.need_human,
          suggested_action: data.suggested_action,
        },
      ]);
      if (data.need_human) setEscalated(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha na IA");
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: "Tive um problema técnico. Melhor você abrir um chamado ou falar pelo WhatsApp.",
          need_human: true,
        },
      ]);
      setEscalated(true);
    } finally {
      setSending(false);
    }
  };

  const openWhatsapp = () => {
    const lastUserMsg =
      [...messages].reverse().find((m) => m.role === "user")?.text || "";
    const body = encodeURIComponent(
      `Olá! Preciso de ajuda com o Ultra. Contexto: ${lastUserMsg.slice(0, 300)}`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${body}`, "_blank");
  };

  return (
    <Card
      className="p-5 rounded-xl border-emerald-200 bg-emerald-50/40"
      data-testid="ai-support-chat"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="h-9 w-9 rounded-md bg-emerald-800 text-white flex items-center justify-center">
          <Bot className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-bold tracking-tight">
            Assistente IA · Primeiro atendimento
          </h3>
          <p className="text-xs text-stone-600 font-body">
            Respostas rápidas para dúvidas do sistema. Encaminha para o WhatsApp se
            precisar de humano.
          </p>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="rounded-md bg-white border border-emerald-100 max-h-80 overflow-y-auto p-3 space-y-3"
        data-testid="ai-support-thread"
      >
        {messages.map((m, i) => {
          const isUser = m.role === "user";
          const Icon = isUser ? UserIcon : Bot;
          return (
            <div
              key={`${i}-${m.role}-${(m.content || "").slice(0, 24)}`}
              className={`flex gap-2 ${isUser ? "flex-row-reverse" : ""}`}
            >
              <div
                className={`h-7 w-7 rounded-full shrink-0 flex items-center justify-center ${
                  isUser ? "bg-stone-200 text-stone-700" : "bg-emerald-800 text-white"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div className={`max-w-[75%] ${isUser ? "items-end" : ""} flex flex-col`}>
                <div
                  className={`rounded-2xl px-3.5 py-2 text-sm font-body whitespace-pre-wrap break-words ${
                    isUser
                      ? "bg-stone-100 text-stone-900"
                      : "bg-emerald-800 text-white"
                  }`}
                  data-testid={`ai-support-message-${i}`}
                >
                  {m.text}
                </div>
                {!isUser && m.suggested_action && (
                  <div className="text-[11px] text-stone-500 font-body mt-1">
                    💡 {m.suggested_action}
                  </div>
                )}
                {!isUser && m.need_human && (
                  <Badge className="bg-amber-100 text-amber-900 border-amber-200 border text-[10px] mt-1 self-start">
                    Recomendado escalar para humano
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
        {sending && (
          <div className="flex items-center gap-2 text-xs text-stone-400 font-body">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> IA digitando…
          </div>
        )}
      </div>

      <div className="mt-3 flex gap-2 items-end">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={2}
          placeholder="Como posso ajudar? Ex: como criar um checklist de temperatura?"
          className="bg-white resize-none flex-1"
          data-testid="ai-support-input"
        />
        <Button
          onClick={send}
          disabled={sending || !input.trim()}
          className="bg-emerald-800 hover:bg-emerald-900 text-white h-11"
          data-testid="ai-support-send"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>

      {escalated && (
        <Button
          onClick={openWhatsapp}
          variant="outline"
          className="mt-2 w-full border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50"
          data-testid="ai-support-whatsapp"
        >
          <MessageCircle className="h-4 w-4 mr-2" /> Falar com humano no WhatsApp
        </Button>
      )}
    </Card>
  );
}
